import { useCallback, useEffect, useMemo } from 'react';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import {
  getContentByIdOptions,
  getContentSearchInfiniteOptions,
  getFilteredContentInfiniteOptions,
  getPersonalizedFeedInfiniteOptions,
} from '@/utils/queryOptions';
import { isSearchable } from '@/utils/contentSearchQuery';
import { filtersToQuery } from '@/modules/content/feed/filters/model/filtersToQuery';
import { isFiltersActive, TContentFilters } from '@/modules/content/feed/filters/model/types';
import { TContent } from '@/types/Content.types';

type Params = {
  filters: TContentFilters;
  // Already-debounced search text. Kept out of the URL by the caller; it reaches the API
  // as a request param and is scrubbed from error reporting.
  query?: string;
};

// The grid's single data source, resolved by precedence:
//
//   1. Search  — a searchable query wins over everything. /search is deterministic and
//                applies the same facets as the filters, so search + filters compose.
//   2. Filters — the deterministic /filtered feed (stable across refreshes).
//   3. Ranked  — the personalized feed (rotates each draw; a refresh mints a new order).
//
// All three queries are declared (rules of hooks) but only the active one is `enabled`,
// so exactly one fetches. Distinct query keys keep their caches from colliding.
const useFeedContent = ({ filters, query = '' }: Params) => {
  const queryClient = useQueryClient();

  const searching = isSearchable(query);
  const filtered = isFiltersActive(filters);
  const facets = useMemo(() => filtersToQuery(filters), [filters]);

  const searchQuery = useInfiniteQuery({
    ...getContentSearchInfiniteOptions(query, facets),
    enabled: searching,
  });

  const filteredQuery = useInfiniteQuery({
    ...getFilteredContentInfiniteOptions(facets),
    enabled: filtered && !searching,
  });

  const personalizedQuery = useInfiniteQuery({
    ...getPersonalizedFeedInfiniteOptions(),
    enabled: !filtered && !searching,
  });

  const source = searching ? searchQuery : filtered ? filteredQuery : personalizedQuery;

  const content = useMemo(
    () => (source.data?.pages.flatMap((page) => page.data) ?? []) as TContent[],
    [source.data?.pages],
  );

  // Seed the per-item detail cache so the loop/article open instantly. Guarded on
  // `details`: if a feed trims card payloads, nothing is seeded and the detail route
  // fetches as normal.
  useEffect(() => {
    content.forEach((item) => {
      if (item.details) queryClient.setQueryData(getContentByIdOptions(item.id).queryKey, item);
    });
  }, [content, queryClient]);

  // A new draw for the ranked feed. Only meaningful when neither searching nor filtering —
  // both of those are deterministic, so refreshing them just refetches the same order.
  const refresh = useCallback(() => {
    queryClient.resetQueries({ queryKey: getPersonalizedFeedInfiniteOptions().queryKey });
  }, [queryClient]);

  return {
    content,
    isSearching: searching,
    isFiltered: filtered,
    // True for personalized only — where "refresh = a different order" makes sense.
    isRanked: !searching && !filtered,
    isLoading: source.isLoading,
    isError: source.isError,
    // keepPreviousData holds the old grid while the next combination loads.
    isTransitioning: source.isPlaceholderData,
    refetch: source.refetch,
    refresh,
    fetchNextPage: source.fetchNextPage,
    hasNextPage: source.hasNextPage,
    isFetchingNextPage: source.isFetchingNextPage,
  };
};

export default useFeedContent;
